import api from './client'
import type { Reading, DifficultyLevel, EnrichmentRun } from '@/types/reading'

interface ReadingInput {
  title: string
  body: string
  difficulty_level: DifficultyLevel
  theme_names: string[]
}

export function fetchReadings(languageId: number) {
  return api.get<{ data: Reading[] }>(`/languages/${languageId}/readings`)
}

export function fetchReading(id: number) {
  return api.get<{ data: Reading }>(`/readings/${id}`)
}

export function createReading(
  languageId: number,
  data: ReadingInput,
) {
  return api.post<{ data: Reading }>(`/languages/${languageId}/readings`, { reading: data })
}

export function updateReading(
  id: number,
  data: ReadingInput,
) {
  return api.patch<{ data: Reading }>(`/readings/${id}`, { reading: data })
}

/**
 * Most recent enrichment run for a reading, or null if none was ever started.
 * Admin-only; the server returns 403 for anyone else.
 */
export function fetchEnrichmentRun(readingId: number) {
  return api.get<{ data: EnrichmentRun | null }>(`/readings/${readingId}/enrichment_run`)
}

/**
 * Start pre-warming a reading's vocabulary. Returns 201 with a new run, or 200 with
 * the run already in flight if one is active.
 */
export function startEnrichmentRun(readingId: number) {
  return api.post<{ data: EnrichmentRun }>(`/readings/${readingId}/enrichment_run`)
}

/**
 * Stop the run in flight. A queued run is cancelled outright; one already working is
 * asked to stop and reports `cancel_requested_at` until it reaches its next word.
 * The record survives with its partial counts. 404 when there was nothing to stop.
 */
export function cancelEnrichmentRun(readingId: number) {
  return api.delete<{ data: EnrichmentRun }>(`/readings/${readingId}/enrichment_run`)
}
